import { useTranslation } from "react-i18next";

import { cn } from "../../lib/utils.js";
import { COUNTER_ICON, COUNTER_TEXT, type CounterSize } from "./counter.js";

/**
 * How many coins a side has settled, and what this round owes it.
 *
 * # Why `pending` is a separate number and not added in
 *
 * ADR 0009 defers settlement past the dispute window: what a round earned is not
 * real until it settles, and a voided round moves nothing. Folding the two
 * together would show a balance that can go *down* when a round is voided, which
 * reads as the game taking coins away — the exact confusion the deferred ledger
 * was designed to avoid. So the settled number is the number, and the pending
 * one is shown beside it as an arriving amount.
 */
export function CoinPurse({
  coins,
  pending = 0,
  chip = false,
  size = "near",
  className,
}: {
  /** Settled coins — the balance that can be spent. */
  coins: number;
  /** What this round has earned but not yet settled (ADR 0009). */
  pending?: number;
  /**
   * Whether this is **your** balance rather than a fact about a side.
   *
   * 🔴 Only one purse on a screen may say yes. The chip was applied to every
   * purse for one pass, and a `SideBoard` header then carried the identical
   * object as the viewer's own balance — two coin chips, one saying what you
   * can spend and one saying what somebody else has, in the same shape. The
   * chip is the affordance for *mine*; a side's coins are a readout.
   */
  chip?: boolean;
  /** How much room there is, and how far away it is read from. */
  size?: CounterSize;
  className?: string;
}) {
  const { t } = useTranslation();
  const icon = COUNTER_ICON[size];

  return (
    <p
      data-testid="coin-purse"
      // ⚠️ **The number, then the coin** — *"the coin icon should be always to
      // the right"*, confirmed against the built screens. It was icon-first
      // from phase 18 until 20d; `CardCount` matches it, because a row with two
      // counts in two orders is a row that has to be read twice.
      //
      // Finding 3.2 made it big enough to read; 20d's second pass makes *your
      // own* balance look like currency. An outline coin and a bare number
      // floating in a header reads as a stray icon — a chip says "this is a
      // balance", and it is the shape every other game the room has played uses
      // for one.
      //
      // The fill is `coin-400/12` under a `coin-400/30` hairline: enough to sit
      // as an object on the deep background without becoming a button, which is
      // the thing it must not look like — nothing here is tappable.
      className={cn(
        "inline-flex items-center text-coin-400",
        COUNTER_TEXT[size],
        chip && "rounded-full border border-coin-400/30 bg-coin-400/12 py-1 pl-3 pr-1.5",
        chip && size === "room" && "py-2 pl-5 pr-3",
        className,
      )}
    >
      <span className="font-semibold tabular-nums">{coins}</span>
      {/* The unit is in the accessible name rather than on screen: at phone size
          the icon carries it, and "12 coins" read aloud beats "12". i18next
          picks the plural form, so a language whose rules differ from English's
          is a translation rather than a code change. */}
      <span className="sr-only">{t("coins.unit", { count: coins })}</span>

      <svg
        width={icon}
        height={icon}
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
        className="shrink-0"
      >
        {/* A struck coin rather than an outlined ring: a filled disc, a darker
            rim, and the milled centre the old icon already had. At 24px the
            fill is what makes it read as metal at a glance. */}
        <circle cx="12" cy="12" r="10" fill="currentColor" fillOpacity="0.85" />
        <circle
          cx="12"
          cy="12"
          r="10"
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.9"
          strokeWidth="1.5"
        />
        <circle
          cx="12"
          cy="12"
          r="5"
          fill="none"
          stroke="var(--color-stage-950)"
          strokeOpacity="0.55"
          strokeWidth="1.5"
        />
      </svg>
      {pending > 0 && (
        <span className="text-xs font-medium text-coin-400/70 tabular-nums">
          +{pending}
          <span className="sr-only"> {t("coins.arriving")}</span>
        </span>
      )}
    </p>
  );
}
